<template>
  <div>
    <Head :title="$t('message.leave.create_leave')" />
    <h1 class="mb-4 text-3xl font-bold">
      <Link class="text-indigo-400 hover:text-indigo-600" href="/leave/leaves">
        {{ $t('message.leave.leave') }}
      </Link>
      <span class="text-indigo-400 font-medium">/</span> {{ $t('message.general.create') }}
    </h1>
    <h5 class="mb-4 text-lg font-bold">
      {{ $t('message.leave.leave_note') }}
    </h5>
    <div class="max-w-3xl bg-white rounded-md shadow overflow-hidden">
      <form @submit.prevent="store">
        <div class="flex flex-wrap -mb-8 -mr-6 p-8">
          <leave-date-picker v-model="form.start_date" :error="form.errors.start_date" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.leave.start_date')" :min="minStartDate" :validator="isBusinessDay" />
          <text-input v-model="form.num_of_leave_days" :error="form.errors.num_of_leave_days" type="number" :max="availableDays" min="1" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.leave.num_of_days')" />
          <select-input v-model="form.leave_type_id" :error="form.errors.leave_type_id" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.leave.leave_type')">
            <option :value="null" />
            <option v-for="leaveType in leave_types" :key="leaveType.id" :value="leaveType.id">
              {{ $t(`message.leave_type.${slugifyLeaveType(leaveType.title)}`) }}
            </option>
          </select-input>
          <text-input v-model="form.leave_address" :error="form.errors.leave_address" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.leave.leave_address')" />
          <text-input v-model="form.leave_phone" :error="form.errors.leave_phone" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.leave.leave_phone')" type="tel" />
          <text-input v-model="form.leave_email" :error="form.errors.leave_email" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.leave.leave_email')" type="email" />
          <div v-if="dateOfResume" class="w-full">
            <p class="pb-8 pr-6 text-gray-600 text-lg">
              {{ $t('message.leave.leave_date_of_resuming') }}
              <span class="font-semibold">{{ formatDate(dateOfResume) }}</span>
            </p>
            <p class="pb-8 pr-6 text-gray-600 text-lg">
              {{ $t('message.leave.leave_end_date') }}
              <span class="font-semibold">{{ formatDate(endDate) }}</span>
            </p>
          </div>
        </div>
        <div class="flex items-center justify-end px-8 py-4 bg-gray-50 border-t border-gray-100">
          <loading-button :loading="form.processing" class="btn-indigo" type="submit" :disabled="!isFormValid">
            {{ $t('message.general.submit') }}
          </loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import LeaveDatePicker from '@/Shared/LeaveDatePicker'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'

export default {
  name: 'CreateLeave',
  components: {
    Head,
    Link,
    LoadingButton,
    SelectInput,
    TextInput,
    LeaveDatePicker,
  },
  layout: Layout,
  props: {
    leave_types: {
      type: Array,
      required: true,
    },
    staffs: {
      type: Array,
      required: true,
    },
    outstanding_days: {
      type: Number,
      required: true,
    },
  },
  remember: 'form',
  data() {
    return {
      form: this.$inertia.form({
        requesting_staff_id: this.$page.props.auth.user.id,
        leave_type_id: null,
        start_date: '',
        end_date: '',
        resume_date: '',
        num_of_leave_days: null,
        leave_address: '',
        leave_phone: '',
        leave_email: '',
      }),
      dateOfResume: '',
      endDate: '',
    }
  },
  computed: {
    minStartDate() {
      const date = new Date()
      date.setMonth(date.getMonth() - 1)
      return date.toISOString().split('T')[0]
    },
    availableDays() {
      return Math.max(0, this.outstanding_days)
    },
    isFormValid() {
      return this.form.start_date && this.form.num_of_leave_days && this.form.leave_type_id && this.form.leave_address && this.form.leave_phone && this.form.leave_email
    },
  },
  watch: {
    'form.start_date': function (newDate) {
      this.updateDateOfResume()
    },
    'form.num_of_leave_days': function (newDays) {
      this.updateDateOfResume()
    },
  },
  methods: {
    updateDateOfResume() {
      if (this.form.start_date && this.form.num_of_leave_days) {
        const { resumeDate, endDate } = this.calculateDates(this.form.num_of_leave_days)
        this.dateOfResume = resumeDate
        this.endDate = endDate
        this.form.end_date = endDate
        this.form.resume_date = resumeDate
      }
    },
    calculateDates(numOfDays) {
      const startDateObj = new Date(this.form.start_date)

      // First adjust start date if it falls on weekend
      this.adjustForWeekend(startDateObj)

      // Count the leave days
      let remainingDays = parseInt(numOfDays)
      const endDate = new Date(startDateObj)

      // Count the actual leave days
      while (remainingDays > 0) {
        if (this.isBusinessDay(endDate)) {
          remainingDays--
        }
        if (remainingDays > 0) {
          endDate.setDate(endDate.getDate() + 1)
        }
      }

      // Calculate resume date (next business day after end date)
      const resumeDate = new Date(endDate)
      resumeDate.setDate(resumeDate.getDate() + 1)
      this.adjustForWeekend(resumeDate)

      // Make sure end date is a business day
      this.adjustForWeekendBackward(endDate)

      return {
        resumeDate: resumeDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
      }
    },
    adjustForWeekendBackward(date) {
      const day = date.getDay()
      if (day === 0) {
        // Sunday
        date.setDate(date.getDate() - 2) // Move to Friday
      } else if (day === 6) {
        // Saturday
        date.setDate(date.getDate() - 1) // Move to Friday
      }
    },
    isBusinessDay(date) {
      if (!(date instanceof Date)) {
        date = new Date(date)
      }
      const day = date.getDay()
      return day !== 0 && day !== 6 // Not Sunday (0) and not Saturday (6)
    },
    adjustForWeekend(date) {
      const day = date.getDay()
      if (day === 0) {
        date.setDate(date.getDate() + 1)
      } else if (day === 6) {
        date.setDate(date.getDate() + 2)
      }
    },
    formatDate(dateString) {
      const locale = this.$t('message.locale')
      const date = new Date(dateString)
      const options = {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }

      return date
        .toLocaleDateString(locale, options)
        .split(' ')
        .map((word, index) => (index === 0 || index === 2 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
        .join(' ')
    },
    store() {
      this.form.post('/leave/leaves')
    },
    slugifyLeaveType(leaveType) {
      return leaveType
        .toLowerCase()
        .replace(/[\s-]+/g, '_')
        .replace(/[^\w-]+/g, '')
    },
  },
}
</script>
